import React, { useEffect, useRef, useState } from "react";
import MUIDataTable from "mui-datatables";
import customHead from 'constants/customHead';
import axios from 'constants/axios';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import PaginationCount from "app/routes/components/PaginationCount";
// Boxed reactstrap form controls, to match the "Create Product" modal in Product Bank (addBank.js).
import { Row, Col, FormGroup, Label, Input, FormText, FormFeedback } from 'reactstrap';
// Same validation stack as Product Bank's Create Product form: react-final-form drives the fields,
// and the two shared reactstrap components turn a field red with its message underneath. Errors
// belong ON the field that is wrong - a toast names one problem at a time and does not point at it.
import { Form, Field, useForm } from 'react-final-form';
import ReactstrapTextField from 'app/routes/components/ReactStrapTextField';
import ReactStrapSelectField from 'app/routes/components/reactstrapSelectField';
// Same camera-tap upload the seller's campaign form uses for its gift picture, so the one feature
// looks and behaves the same on both sides.
import FieldFileInput from 'app/routes/components/FieldFileInput';
// `number` tolerates an empty value, `number0` additionally rejects zero. The caps are optional one
// by one (only the set of three is mandatory), so they take `number` - `number0` reads '' as 0 and
// would red-flag a cap the admin simply chose not to use.
import { required, number, number0 } from 'constants/validations';

const STATUS_OPTIONS = ['all', 'draft', 'pending', 'active', 'paused', 'closed', 'expired', 'rejected'];
const TYPE_OPTIONS = ['all', 'coupon', 'cashback', 'gift'];
// Granularity of the gift delivery cost the admin agrees at approval: at least this much, and a
// whole multiple of it. Mirrors GIFT_DELIVERY_COST_STEP in medimny-api controllers/productCoupon.js,
// which is the copy that actually decides - this one exists to disable the button rather than let
// the admin submit a value the API will only refuse.
const GIFT_DELIVERY_COST_STEP = 25;

// Stored as the close reason when an admin closes a campaign without typing one. The field is
// optional on this side - an admin closing someone else's campaign often has nothing to add beyond
// the act itself - but the API requires SOME reason (validateCloseReason in
// controllers/productCoupon.js), and a row reading "Closed by admin -" would be worse than this.
// Phrased like the system's own strings so it is recognisable as a default rather than as something
// an admin actually wrote.
const ADMIN_DEFAULT_CLOSE_REASON = 'Admin closed the campaign.';

// null when the value is acceptable, else the reason it is not. Drives BOTH the disabled state of
// the Approve button and the message under the field, so the two can never disagree.
const giftDeliveryCostError = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return 'Delivery cost is required.';
  const n = Number(raw);
  if (!isFinite(n)) return 'Enter a number.';
  if (n < GIFT_DELIVERY_COST_STEP) return `Must be at least ₹${GIFT_DELIVERY_COST_STEP}.`;
  if (n % GIFT_DELIVERY_COST_STEP !== 0) return `Must be a multiple of ₹${GIFT_DELIVERY_COST_STEP}.`;
  return null;
};
const TIER_OPTIONS = ['all', 'product', 'seller', 'platform'];
const PER_PAGE = 50;
const DEFAULT_FILTER = { search: '', status: 'all', type: 'all', tier: 'all', from: '', to: '' };
const DEFAULT_DRAFT = { status: 'all', type: 'all', tier: 'all', from: '', to: '' };

const TIER_LABEL = {
  all: 'All',
  product: 'Product level (seller funded)',
  seller: 'Seller level (seller funded)',
  platform: 'Platform level (MEDIMNY funded)'
};

// A platform campaign is Medimny's own money. Everything about it is entered here by the admin.
const EMPTY_PLATFORM = {
  // a platform campaign is a value tier, so its discount reward is a 'cashback', not a 'coupon'
  type: 'cashback',
  min_order_value: '',
  cashback_type: 'flat',
  cashback_value: '',
  cashback_percent: '',
  cashback_floor: '',
  cashback_max: '',
  gift_description: '',
  gift_price: '',
  gift_brand: '',
  gift_image: '',
  budget_total: '',
  order_count_limit: '',
  end_date: ''
};

const statusStyle = (s) => ({
  active: 'text-white bg-success',
  pending: 'text-white bg-warning',
  paused: 'text-white bg-info',
  rejected: 'text-white bg-danger',
  closed: 'text-white bg-grey',
  expired: 'text-white bg-grey',
  draft: 'text-white bg-grey'
}[s] || 'text-white bg-grey');

const cap = (s) => (s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1));
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// Always two decimals. Used wherever a figure is being compared against another figure - a rounding
// note is unreadable if one side prints as "100.5" and the other as "101".
const money2 = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/*
The band a percentage cashback pays in, in words: "at least X" for the guaranteed floor and "up to Y"
for the ceiling above which nothing more is earned. Returns the inside of the bracket, or '' when the
campaign carries neither end.

Both halves are conditional. Only the 'minimum' type guarantees a floor, and a campaign approved
before `cashback_max` existed carries no ceiling - so a floored campaign reads "at least ₹150, up to
₹500", a plain percentage reads "up to ₹500", and a legacy one falls back to no bracket at all rather
than printing "up to ₹0", which would read as a cashback of nothing.

Same wording as the buyer side (the cart offer banners and the view-seller offer panel), so a seller
reading their Reward column sees the offer described exactly as their buyers do.
*/
const cashbackBand = (type, floor, max) => [
  type === 'minimum' && Number(floor) > 0 ? `at least ${money(floor)}` : '',
  Number(max) > 0 ? `up to ${money(max)}` : ''
].filter(Boolean).join(', ');

// A seller-funded gift campaign that has been through approval. Its delivery cost is settled: either
// it carries one, or it never will. Mirrors GIFT_COST_AGREED_STATUSES in the API's productCoupon
// controller, which is what decides whether the row is priced at all.
const isApprovedGift = (row) => row.type === 'gift' && row.tier !== 'platform'
  && ['active', 'paused', 'closed', 'expired'].includes(row.status);

/*
The total budget is stored as whole rupees - the API rounds it in createCampaign/editCampaign.

Budget is only ever drawn down by amounts that are themselves whole rupees (a cashback is rounded by
computeCashback, a coupon value is a multiple of 5), so paise on the cap could only ever sit there as
a remainder nothing can spend. Rounding it makes the cap mean what it says.

Returns null unless the figure actually has decimals - an integer budget is stored exactly as typed.
*/
const budgetRoundingNote = (values) => {
  const b = Number(values.budget_total);
  if (!isFinite(b) || b <= 0 || Number.isInteger(b)) return null;
  return `Budget will be saved as ${money2(Math.round(b))} - it is rounded to the nearest rupee.`;
};

/*
The LEAST a single qualifying order can draw from a platform cashback - the divisor behind its order
count limit. Mirrors `cashbackPerOrderMinimum` in the API's controllers/productCoupon.js, and the
seller form's copy in medimny-web myCoupons.

  flat                   - the fixed amount every qualifying order takes; least and most are one.
  proportionate          - the payout at exactly the minimum order value (MOV x %). No qualifying
                           order is smaller, so none can earn less.
  minimum (pct + floor)  - the guaranteed minimum: a small order draws the floor rather than its
                           percentage.

The MAX cashback is deliberately not used. It bounds what one order can COST, not how many orders the
budget can reach - and the count is a cap the admin chooses within what the money could fund, not a
forecast of what it will fund.
*/
const cashbackPerOrderMinimum = (values) => {
  if (values.cashback_type === 'flat') return Number(values.cashback_value);
  if (values.cashback_type === 'minimum') return Number(values.cashback_floor);
  if (values.cashback_type === 'proportionate') {
    return (Number(values.min_order_value) * Number(values.cashback_percent)) / 100;
  }
  return NaN;
};

// The most orders a platform cashback's budget could ever pay out on - the CEILING on its order count
// limit, and what that field is filled in with while it is empty. The API checks the same figure.
//
// Rounded first so it matches the rounded budget the API stores. Returns '' until both figures are
// present.
const derivedCashbackOrderLimit = (values) => {
  const budget = Math.round(Number(values.budget_total));
  const perOrder = cashbackPerOrderMinimum(values);
  if (!Number.isFinite(budget) || budget <= 0) return '';
  if (!Number.isFinite(perOrder) || perOrder <= 0) return '';
  return Math.floor(budget / perOrder);
};

/*
The order count field, reddened as it is typed rather than on blur.

The shared ReactStrapTextField only shows an error once the field is `touched`, i.e. after the focus
leaves it. That is fine where an entry is judged after it is finished, but this count is refilled from
the budget under the admin's hands, and an entry above what the budget funds is wrong the moment it is
that - waiting for a blur leaves a red-worthy figure sitting there looking accepted. `modified` is true
from the first keystroke, so adding it to `touched` says it straight away. Same as the seller form's
RSText, which reddens on either.

Only this field is wrapped: the rest of the form is judged on blur like every other admin screen.
*/
const OrderCountField = (props) => (
  <ReactstrapTextField {...props} meta={{ ...props.meta, touched: props.meta.touched || props.meta.modified }} />
);

/*
Fills the order count in from what the budget funds, and keeps it there.

Every change to the figures the count is divided out of refills the field, so the number in the box is
always the maximum the campaign can carry. Which figures those are depends on the cashback type:

  flat                  - budget / cashback amount
  percentage            - budget / (min order value x cashback %)
  percentage with floor - budget / the guaranteed minimum, which is the least a small order can draw

The count stays the admin's to set - they may want the campaign to stop sooner than the money runs out
- so a lower figure typed under the derived one stands, and survives until one of the figures above
moves again. It is a starting point that is kept current, not a locked value.

Refilling rather than leaving a stale count is the point: `orderLimitWithinBudgetRule` would reject a
count the budget no longer reaches, so the alternative to refilling is refusing on save. And on the
percentage types the divisor is assembled as the figures are typed - minimum order value, percentage,
floor - so a count that was right when it was typed stops being right two fields later.

The first render never overwrites, which is what keeps an edited campaign's saved count intact.

Kept in step with the seller form's copy in medimny-web myCoupons - the two forms author the same kind
of campaign against the same API. Never rendered in view mode, where the form shows the campaign as
stored.
*/
const PrefillOrderLimit = ({ value }) => {
  const form = useForm();
  const firstRender = useRef(true);
  useEffect(() => {
    const first = firstRender.current;
    firstRender.current = false;
    if (value === '') return;
    const current = form.getState().values.order_count_limit;
    const isEmpty = current === undefined || current === null || current === '';
    // Never overwritten on the first render: the derived figure is computed from the values the form
    // OPENS with, so overwriting then would throw away the count an edited campaign was saved with
    // before anything has been touched. From then on `value` only changes because the budget or one of
    // the cashback figures did, which is exactly when it should refill.
    if (isEmpty || !first) {
      form.change('order_count_limit', value);
    }
  }, [value, form]);
  return null;
};

/*
What this campaign will ACTUALLY credit, when that differs from what has been typed.

A cashback is credited to the buyer's wallet as a whole number of rupees - the API rounds it in
models/productCoupon.js `computeCashback`, and that rounded figure is also what is recovered from the
funder. So a decimal entered here is not the amount anyone will see, and the author should learn that
while they can still change it rather than from a settlement line later.

Returns null when there is nothing to warn about, so the note simply does not render.

- flat: only when the amount actually has decimals - an integer flat cashback is credited exactly as
  typed, and a note there would be noise.
- percentage (with or without a floor): shown as soon as a percent is entered, because a percentage
  of an arbitrary order value lands on a whole rupee only by luck. Worked at the minimum order value,
  the one order size that IS known in advance, so the note names a real figure instead of a rule.
*/
const cashbackRoundingNote = (values) => {
  const type = values.cashback_type;
  if (type === 'flat') {
    const v = Number(values.cashback_value);
    if (!isFinite(v) || v <= 0 || Number.isInteger(v)) return null;
    return `${money2(v)} will be credited as ${money2(Math.round(v))} - cashback is rounded to the nearest rupee.`;
  }
  const pct = Number(values.cashback_percent);
  if (!isFinite(pct) || pct <= 0) return null;
  const parts = ['Cashback is rounded to the nearest rupee when it is credited.'];
  const mov = Number(values.min_order_value);
  if (isFinite(mov) && mov > 0) {
    const atMov = (mov * pct) / 100;
    // Only worth working through when the example actually rounds. "₹150.00 would be credited as
    // ₹150.00" teaches nothing and makes the sentences that DO matter harder to notice.
    if (!Number.isInteger(atMov)) {
      parts.push(`At the ${money(mov)} minimum, ${pct}% = ${money2(atMov)} would be credited as ${money2(Math.round(atMov))}.`);
    }
  }
  if (type === 'minimum') {
    const floor = Number(values.cashback_floor);
    if (isFinite(floor) && floor > 0 && !Number.isInteger(floor)) {
      parts.push(`The ${money2(floor)} guaranteed minimum would be credited as ${money2(Math.round(floor))}.`);
    }
  }
  return parts.join(' ');
};

const authHeader = () => ({ headers: { token: localStorage.getItem('admin_token') } });

const composeValidators = (...validators) => (value, allValues) =>
  validators.reduce((error, validator) => error || validator(value, allValues), undefined);

/*
Caps every numeric entry on this screen at 2 decimal places. Same rule and same helper as the seller
form (medimny-web components/myCoupons), so a campaign is entered the same way whoever creates it.

Applied as react-final-form's `parse` rather than as a validator, so the third decimal never lands in
the field - the admin is stopped from typing it instead of being told off for it afterwards. Only a
value that already HAS more than two decimals is rewritten; half-typed entries ('12', '12.', '12.3')
pass through untouched, and '' becomes undefined the way final-form's own default parse does.
*/
const twoDecimals = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const tooPrecise = String(value).match(/^(-?\d*\.\d{2})\d+$/);
  return tooPrecise ? tooPrecise[1] : value;
};

// ---- platform-campaign field rules; these mirror validateValueTier on the API. The admin sets the
// minimum and the amounts freely (Medimny funds it), so the only rules are coherence ones. ----

// a cashback larger than the order that unlocks it would drive the payable to zero
const notAboveMovRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const mov = Number(allValues && allValues.min_order_value);
  if (Number.isFinite(mov) && mov > 0 && Number(value) > mov) {
    return 'Cannot be greater than the minimum order value';
  }
  return undefined;
};

const percentRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const v = Number(value);
  if (v > 100) return 'Cannot be more than 100%';
  return undefined;
};

// Cashback earned on an order AT the minimum order value (MOV x cashback %). Both the floor and the
// max are assessed against it, so the two percentage types share this helper.
const movCashbackOf = (allValues) => {
  const mov = Number(allValues && allValues.min_order_value);
  const pct = Number(allValues && allValues.cashback_percent);
  if (!Number.isFinite(mov) || mov <= 0 || !Number.isFinite(pct) || pct <= 0) return null;
  return (mov * pct) / 100;
};

// The guaranteed minimum (percentage-with-floor only) must cover at least what a MOV-sized order earns
// by percentage - otherwise the "guarantee" is below the very floor of the percentage it sits on.
const minCashbackRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const movCashback = movCashbackOf(allValues);
  if (movCashback !== null && Number(value) < movCashback) {
    return `Must be at least ₹${Math.ceil(movCashback)} (min order value x cashback %)`;
  }
  return undefined;
};

// The max cashback (both percentage types) caps the per-order payout. It must cover at least what an
// order AT the minimum order value earns (MOV x cashback %), and on the percentage-with-floor type it
// must also not sit below the guaranteed minimum. Mirrors the API's proportionate / minimum branches.
const maxCashbackRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const movCashback = movCashbackOf(allValues);
  if (movCashback !== null && Number(value) < movCashback) {
    return `Must be at least ₹${Math.ceil(movCashback)} (min order value x cashback %)`;
  }
  if (allValues && allValues.cashback_type === 'minimum') {
    const floor = Number(allValues.cashback_floor);
    if (Number.isFinite(floor) && floor > 0 && Number(value) < floor) {
      return 'Cannot be less than the guaranteed minimum';
    }
  }
  return undefined;
};

// A budget on a percentage cashback (proportionate or minimum) must be able to cover at least one max
// payout, so it cannot sit below the ceiling. Reddens the budget field and blocks submit; the campaign
// then auto-closes once the leftover budget falls back below the ceiling (API side).
const budgetAboveMaxRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (!allValues || (allValues.cashback_type !== 'proportionate' && allValues.cashback_type !== 'minimum')) return undefined;
  const max = Number(allValues.cashback_max);
  if (!Number.isFinite(max) || max <= 0) return undefined;   // the max field's own rule flags a missing max
  if (Math.round(Number(value)) < max) return 'Total budget must be greater than or equal to the max cashback';
  return undefined;
};

// An order count is a number of orders. Validated rather than parsed away as it is typed, so an
// admin who enters 2.5 is told what is wrong instead of watching the field rewrite itself. The API
// floors whatever arrives, so this is the form saying the same thing first.
const wholeOrdersRule = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return Number.isInteger(Number(value)) ? undefined : 'Must be a whole number of orders';
};

/*
The order count limit a campaign may carry: no more orders than its budget could ever pay for.
Mirrors the same check in the API's createCampaign/editCampaign, so the form refuses what the save
would refuse.

A GIFT is exempt - it spends no budget, so there is nothing to hold its count to and the count is the
only cap it has. Same when no budget is set. The rule stays silent until the figures it divides are on
the form: until then there is no maximum to hold the entry to.
*/
const orderLimitWithinBudgetRule = (value, allValues) => {
  if (value === undefined || value === null || value === '') return undefined;
  const values = allValues || {};
  if (values.type === 'gift') return undefined;
  const funded = derivedCashbackOrderLimit(values);
  if (funded === '') return undefined;
  if (Number(value) > funded) {
    // No figure is named, and the wording is the seller form's. On a flat cashback the field is
    // refilled from the budget and the amount on every change to either, so the maximum is already
    // the number sitting in the box - repeating it here would only say the same thing twice.
    return 'Max order count limit should be within budget';
  }
  return undefined;
};

// The picture is mandatory on a gift campaign. Same rule and same wording as the seller form's
// `requiredImage` (medimny-web constants/validations), which this app has no equivalent of.
const requiredGiftImage = (value) => (value ? undefined : 'Please upload image');


function ProductCouponsList() {
  const [applied, setApplied] = useState(DEFAULT_FILTER);   // committed filter (incl. search)
  const [draft, setDraft] = useState(DEFAULT_DRAFT);         // edited inside the filter popover
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loader, setLoader] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Closing keeps its own dialog rather than routing through `confirm` below, for the same reason
  // Reject does: it collects free text. The generic confirmation's input is a numeric one (it exists
  // to take a gift delivery cost), so a reason cannot be typed into it.
  //
  // Optional, like a rejection reason. The API requires the field, so a blank one is submitted as
  // ADMIN_DEFAULT_CLOSE_REASON - the record still says who ended the campaign and that it was ended
  // by hand rather than by a threshold, which is what the field is read for. The seller's own close
  // dialog still demands a typed reason: they are closing their own campaign and are the only party
  // who knows why.
  const [closeFor, setCloseFor] = useState(null);
  const [closeReason, setCloseReason] = useState('');
  const [searchedText, setSearchedText] = useState('');
  // The create form's own values live in react-final-form, not here - it is mounted fresh with
  // EMPTY_PLATFORM each time the dialog opens, and `submitting` is what disables the button.
  const [createOpen, setCreateOpen] = useState(false);
  // The platform campaign being edited (opened from its row's Edit action). Only a DRAFT can be
  // edited - once launched the terms are locked - so this is only ever set on a draft. null => closed.
  const [editRow, setEditRow] = useState(null);
  // The campaign whose full detail is shown read-only (opened by clicking a scope). null => closed.
  const [viewRow, setViewRow] = useState(null);
  // A pending irreversible action awaiting confirmation. Holds { title, message, confirmLabel,
  // danger, run }; every approve/launch/close/abort routes through this so a mis-click can't act on a
  // live campaign. Reject keeps its own dialog (it also collects a reason). null => nothing pending.
  const [confirm, setConfirm] = useState(null);
  // The value typed into the confirmation's input, when it has one (only Approve-a-gift does today).
  // Reset by whichever ask* opens the dialog, so a value typed and cancelled cannot leak into the
  // next campaign the admin approves.
  const [confirmInput, setConfirmInput] = useState('');
  // NOTE: there is no page-level gift delivery figure here on purpose. The cost is PER SELLER - the
  // admin can set a rate for one seller in their Coupon Config dialog - so one number held for the
  // whole list would misreport every campaign belonging to a seller who has an override. Each row
  // carries its own `gift_delivery_amount`, resolved server-side by adminListCampaigns through the
  // same helper the settlement deduction uses.

  const setDraftField = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const fetchList = async (p = 0, f = applied) => {
    setLoader(true);
    try {
      const body = { page: p + 1, perPage: PER_PAGE };
      if (f.status && f.status !== 'all') body.status = f.status;
      if (f.type && f.type !== 'all') body.type = f.type;
      if (f.tier && f.tier !== 'all') body.tier = f.tier;
      if (f.search) body.searchText = f.search;
      if (f.from) body.from = f.from;
      if (f.to) body.to = f.to;
      const res = await axios.post('productCoupon/admin/list', body, authHeader());
      const data = res && res.data;
      if (data && !data.error) {
        setRows(data.detail || []);
        setTotal(data.metadata && data.metadata.total ? data.metadata.total : 0);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Failed to load campaigns');
      }
    } catch (e) {
      NotificationManager.error('Failed to load campaigns');
    }
    setLoader(false);
  };

  useEffect(() => {
    fetchList(0, DEFAULT_FILTER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePage = (p) => {
    setPage(p);
    fetchList(p, applied);
  };

  const handleSearch = (text) => {
    const next = { ...applied, search: text || '' };
    setApplied(next);
    setSearchedText(text || '');
    setPage(0);
    fetchList(0, next);
  };

  const applyDraft = () => {
    const next = { ...applied, ...draft };
    setApplied(next);
    setPage(0);
    fetchList(0, next);
  };

  const resetDraft = () => {
    const next = { ...applied, ...DEFAULT_DRAFT };
    setDraft(DEFAULT_DRAFT);
    setApplied(next);
    setPage(0);
    fetchList(0, next);
  };

  const act = async (url, body, okMsg) => {
    try {
      const res = await axios.post(url, body, authHeader());
      const data = res && res.data;
      if (data && !data.error) {
        NotificationManager.success(okMsg);
        fetchList(page, applied);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Action failed');
      }
    } catch (e) {
      NotificationManager.error('Action failed');
    }
  };

  // Approving a GIFT campaign is also the moment its delivery cost is agreed - the API requires it
  // and rejects the approval without a valid one, so it is sent as part of this call rather than
  // saved separately. Non-gift campaigns send nothing: they deliver nothing, so they cost nothing.
  const approve = (row, giftDeliveryCost) => act(
    'productCoupon/admin/approve',
    row.type === 'gift'
      ? { campaign_id: row._id, gift_delivery_cost: Number(giftDeliveryCost) }
      : { campaign_id: row._id },
    'Campaign approved & launched'
  );
  // The reason travels with the close rather than being saved separately - the API requires it and
  // refuses the call without one, exactly as approving a gift carries its delivery cost.
  const close = (row, reason) => act('productCoupon/admin/close', { campaign_id: row._id, close_reason: reason }, 'Campaign closed');
  // Platform campaigns are created as drafts; the admin then launches (-> live) or aborts them.
  const launch = (row) => act('productCoupon/admin/launch', { campaign_id: row._id }, 'Campaign launched');
  const abort = (row) => act('productCoupon/admin/abort', { campaign_id: row._id }, 'Campaign aborted');
  // Any live campaign can be paused (stops awarding until resumed) and later resumed. A campaign
  // whose end date passes is auto-closed by the API, whether it was running or paused.
  const pause = (row) => act('productCoupon/admin/pause', { campaign_id: row._id }, 'Campaign paused');
  const resume = (row) => act('productCoupon/admin/resume', { campaign_id: row._id }, 'Campaign resumed');

  // Each button opens a confirmation first; the actual call above only runs once the admin confirms.
  /*
  Approving a gift campaign additionally COLLECTS its delivery cost, so this confirmation carries an
  input the others do not. The cost is per campaign - it is what this seller is billed for every
  order that carries this gift, for as long as the campaign runs - and there is no screen to change
  it later, which is why the dialog says so and why the field cannot be skipped.

  A coupon or cashback campaign delivers nothing, so it is approved with a plain confirmation.
  */
  const askApprove = (row) => {
    setConfirmInput('');
    setConfirm({
      title: 'Approve campaign',
      message: 'Are you sure you want to approve this campaign? It will be launched and go live immediately.',
      confirmLabel: 'Approve',
      danger: false,
      input: row.type === 'gift' ? {
        label: 'Gift Delivery Cost (₹)',
        help: `Charged to the seller for every order carrying this gift, plus GST. Minimum ₹${GIFT_DELIVERY_COST_STEP}, in multiples of ₹${GIFT_DELIVERY_COST_STEP}. It is fixed once the campaign is approved.`,
        validate: giftDeliveryCostError
      } : null,
      run: (value) => approve(row, value)
    });
  };
  const askLaunch = (row) => setConfirm({
    title: 'Launch campaign',
    message: 'Are you sure you want to launch this campaign? It will go live immediately.',
    confirmLabel: 'Launch', danger: false, run: () => launch(row)
  });
  const askClose = (row) => {
    setCloseReason('');
    setCloseFor(row);
  };
  const askAbort = (row) => setConfirm({
    title: 'Abort campaign',
    message: 'Are you sure you want to abort this campaign? The draft will be discarded and cannot be undone.',
    confirmLabel: 'Abort', danger: true, run: () => abort(row)
  });
  const askPause = (row) => setConfirm({
    title: 'Pause campaign',
    message: 'Are you sure you want to pause this campaign? It will stop awarding discounts/gifts until you resume it.',
    confirmLabel: 'Pause', danger: false, run: () => pause(row)
  });
  const askResume = (row) => setConfirm({
    title: 'Resume campaign',
    message: 'Are you sure you want to resume this campaign? It will go live again immediately.',
    confirmLabel: 'Resume', danger: false, run: () => resume(row)
  });

  /*
  Every field rule now lives on the field itself (see the validators above and the `validate` props
  in the dialog), so by the time this runs the values are already coherent - it only has to shape
  them for the API. A toast is kept for the API's own verdict, which is the one thing the form
  cannot know in advance.
  */
  // Shape the form values into the reward payload the API expects. Shared by create and edit so the
  // two stay in lock-step - the only difference between them is the envelope (tier vs campaign_id).
  const rewardBody = (values) => {
    const isGiftReward = values.type === 'gift';
    const body = {
      type: values.type,
      min_order_value: Number(values.min_order_value),
      // A gift is not paid for in rupees, so it carries no spend cap - the order count and the end
      // date are its only thresholds, and the budget field is not rendered for it. Sent as 0 rather
      // than as whatever was typed before the reward was switched to a gift.
      budget_total: !isGiftReward && values.budget_total ? Number(values.budget_total) : 0,
      order_count_limit: values.order_count_limit ? Number(values.order_count_limit) : 0,
      end_date: values.end_date || undefined
    };
    if (isGiftReward) {
      body.gift = {
        description: values.gift_description,
        price: Number(values.gift_price),
        brand: values.gift_brand,
        image: values.gift_image || ''
      };
    } else {
      body.cashback_type = values.cashback_type;
      if (values.cashback_type === 'flat') {
        body.cashback_value = Number(values.cashback_value);
      } else {
        body.cashback_percent = Number(values.cashback_percent);
        if (values.cashback_type === 'minimum') body.cashback_floor = Number(values.cashback_floor);
        // both percentage types carry a mandatory ceiling on the per-order payout
        if (values.cashback_type === 'proportionate' || values.cashback_type === 'minimum') body.cashback_max = Number(values.cashback_max);
      }
    }
    return body;
  };

  const submitPlatform = async (values) => {
    const body = { tier: 'platform', ...rewardBody(values) };
    try {
      const res = await axios.post('productCoupon/create', body, authHeader());
      const data = res && res.data;
      if (data && !data.error) {
        // created as a draft - the admin launches it from the list, so it is NOT live yet
        NotificationManager.success(data.title || 'Platform campaign created');
        setCreateOpen(false);
        setPage(0);
        fetchList(0, applied);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Could not create campaign');
      }
    } catch (e) {
      NotificationManager.error('Could not create campaign');
    }
  };

  // Prefill the form from an existing platform draft, undoing what rewardBody/submit did on the way
  // in: numbers back to strings the inputs can hold, the end date back to YYYY-MM-DD, and the stored
  // gift image URL kept as-is (FieldFileInput shows it as the current thumbnail).
  const platformInitialValues = (row) => ({
    type: row.type || 'cashback',
    min_order_value: row.min_order_value != null ? String(row.min_order_value) : '',
    cashback_type: row.cashback_type || 'flat',
    cashback_value: row.cashback_value != null ? String(row.cashback_value) : '',
    cashback_percent: row.cashback_percent != null ? String(row.cashback_percent) : '',
    cashback_floor: row.cashback_floor != null ? String(row.cashback_floor) : '',
    cashback_max: row.cashback_max != null ? String(row.cashback_max) : '',
    gift_description: (row.gift && row.gift.description) || '',
    gift_price: row.gift && row.gift.price != null ? String(row.gift.price) : '',
    gift_brand: (row.gift && row.gift.brand) || '',
    gift_image: (row.gift && row.gift.image) || '',
    budget_total: row.budget_total > 0 ? String(row.budget_total) : '',
    order_count_limit: row.order_count_limit > 0 ? String(row.order_count_limit) : '',
    end_date: row.end_date ? moment(row.end_date).format('YYYY-MM-DD') : ''
  });

  // Save edits to a platform draft. The reward TYPE is fixed once created (the API edit path keeps
  // the stored type), which is why the form locks that one field in edit mode.
  const submitEdit = async (values) => {
    const body = { campaign_id: editRow._id, ...rewardBody(values) };
    try {
      const res = await axios.post('productCoupon/edit', body, authHeader());
      const data = res && res.data;
      if (data && !data.error) {
        NotificationManager.success(data.title || 'Campaign updated');
        setEditRow(null);
        fetchList(page, applied);
      } else {
        NotificationManager.error(data && data.title ? data.title : 'Could not update campaign');
      }
    } catch (e) {
      NotificationManager.error('Could not update campaign');
    }
  };
  const submitReject = () => {
    const id = rejectFor._id;
    const reason = rejectReason;
    setRejectFor(null);
    setRejectReason('');
    act('productCoupon/admin/reject', { campaign_id: id, rejection_reason: reason }, 'Campaign rejected');
  };
  const submitClose = () => {
    const row = closeFor;
    // Blank is allowed here and stands for the default: the API will not take an empty reason, so the
    // substitution happens on the way out rather than leaving the admin to type something themselves.
    const reason = closeReason.trim() || ADMIN_DEFAULT_CLOSE_REASON;
    if (!row) return;
    setCloseFor(null);
    setCloseReason('');
    close(row, reason);
  };

  // A seller-level campaign is store-wide: it rewards a cashback (or a gift) on any order clearing
  // its minimum order value, and it has no product at all.
  const cashbackText = (row) => {
    if (row.cashback_type === 'flat') return `${money(row.cashback_value)} cashback`;
    // Both percentage types are described the same way - the band they pay in, floor and ceiling.
    if (row.cashback_type === 'proportionate' || row.cashback_type === 'minimum') {
      const band = cashbackBand(row.cashback_type, row.cashback_floor, row.cashback_max);
      return band
        ? `${row.cashback_percent}% cashback (${band})`
        : `${row.cashback_percent}% cashback`;
    }
    return '-';
  };

  const isValueTier = (row) => row.tier === 'seller' || row.tier === 'platform';

  const valueCell = (row) => {
    const overMov = isValueTier(row) && row.min_order_value > 0
      ? <span className="text-muted" style={{ fontSize: '12px' }}> on orders over {money(row.min_order_value)}</span>
      : null;
    if (row.type === 'gift') {
      const gift = `🎁 ${row.gift && row.gift.description ? row.gift.description : '-'}${row.gift && row.gift.price != null ? ` worth ${money(row.gift.price)}` : ''}${row.gift && row.gift.brand ? ` (${row.gift.brand})` : ''}`;
      /*
      What THIS campaign's seller is charged per gift order at settlement: its own delivery cost plus
      GST. The API stamps `gift_delivery_amount` on the row, and ONLY once the campaign has been
      approved - which is the moment that cost is agreed. Two approved rows can legitimately show
      different amounts, including two from the same seller.

      Before approval there is no figure to show, so the row says the cost is still to be set rather
      than printing one. Naming a number on a pending row would be naming the global fallback as
      though it were this campaign's rate - and it is the very number the admin is about to choose in
      the approval dialog.

      A platform gift is Medimny-funded, carries no seller charge, and is sent without the field, so
      it shows neither. There is no enablement fee anywhere.
      */
      const isSellerFundedGift = row.tier !== 'platform';
      const fee = Number(row.gift_delivery_amount) > 0
        ? <span className="text-muted" style={{ fontSize: '12px' }}> · {money2(row.gift_delivery_amount)} gift delivery cost incl. GST per gift order</span>
        : (isSellerFundedGift && row.status === 'pending'
          ? <span className="text-muted" style={{ fontSize: '12px' }}> · gift delivery cost is set on approval</span>
          : null);
      return <span>{gift}{overMov}{fee}</span>;
    }
    if (isValueTier(row)) {
      return <span><strong>{cashbackText(row)}</strong>{overMov}</span>;
    }
    return <strong>{money(row.coupon_value)} off</strong>;
  };

  // Rendering a bare '-' for a campaign that legitimately has no product reads as a bug. Say so -
  // and on a platform campaign, say WHOSE MONEY it is, because that is the one thing about it that
  // can never be mistaken: the seller is settled as if it never happened, and Medimny eats the cost.
  // The scope is clickable: it opens the campaign's full detail read-only, laid out like the
  // create-campaign popup.
  const scopeCell = (row) => {
    let content;
    if (row.tier === 'platform') {
      content = (
        <span>
          <span className="badge bg-danger text-white" style={{ fontSize: '11px' }}>MEDIMNY FUNDED</span>
          <div className="text-muted" style={{ fontSize: '12px' }}>All sellers, site-wide</div>
        </span>
      );
    } else if (row.tier === 'seller') {
      content = <span>All products <span className="text-muted" style={{ fontSize: '12px' }}>(store-wide)</span></span>;
    } else {
      content = <span>{row.product_id && row.product_id.name ? row.product_id.name : '-'}</span>;
    }
    return (
      <span
        className="text-primary"
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => setViewRow(row)}
        title="View campaign details"
      >
        {content}
      </span>
    );
  };

  const validity = (row) => {
    const start = row.start_date ? moment(row.start_date).format('DD MMM YY') : '-';
    const end = row.end_date ? moment(row.end_date).format('DD MMM YY') : 'No end';
    return `${start} → ${end}`;
  };

  // One read-only field for the campaign-detail popup, styled like the create form's boxed inputs.
  const viewField = (label, value, sm = 6) => (
    <Col xs={12} sm={sm}>
      <FormGroup>
        <Label>{label}</Label>
        <Input type="text" value={value === null || value === undefined || value === '' ? '-' : String(value)} readOnly disabled />
      </FormGroup>
    </Col>
  );

  // The reward-specific fields differ by tier/type, so the detail popup mirrors the create form's
  // conditional layout: gift fields, cashback fields (value tiers), or a flat coupon value (product).
  const rewardFields = (row) => {
    if (row.type === 'gift') {
      return (
        <React.Fragment>
          {viewField('Gift description', row.gift && row.gift.description, 12)}
          {viewField('Gift price (₹)', row.gift && row.gift.price)}
          {viewField('Gift brand', row.gift && row.gift.brand)}
          {/* What was agreed when this campaign was approved, ex-GST, and the incl-GST figure the
              seller is actually deducted per gift order.

              A platform gift is Medimny-funded and carries no seller charge, so neither is shown.
              An unapproved campaign has no cost yet and says so - the admin sets it in the approval
              dialog. A campaign approved before the cost moved onto the campaign has none either,
              and since there is no global rate any more it is charged NOTHING for gift delivery -
              which the row states outright rather than leaving as a blank to be misread. */}
          {row.tier !== 'platform' ? viewField(
            'Gift delivery cost (₹, ex-GST)',
            row.gift_delivery_cost > 0
              ? row.gift_delivery_cost
              : (isApprovedGift(row) ? 'None recorded (approved before this was required)' : 'Set when the campaign is approved')
          ) : null}
          {row.tier !== 'platform' ? viewField(
            'Charged per gift order (incl. GST)',
            row.gift_delivery_amount > 0
              ? money2(row.gift_delivery_amount)
              : (isApprovedGift(row) ? 'Nothing' : 'Not yet approved')
          ) : null}
          {row.gift && row.gift.image ? (
            <Col xs={12} className="mt-2">
              {/* Read-only, but laid out like the seller-side FieldFileInput: the (non-interactive)
                  select icon with its label on the left corner and the image preview on the right. */}
              <div className="d-flex align-items-center justify-content-between" style={{ gap: 16 }}>
                <div className="text-center">
                  <img alt="Gift image" src={require('assets/images/camera_3.png')} className="d-block mx-auto" />
                  <label>Gift image</label>
                </div>
                <img
                  src={row.gift.image}
                  alt="Gift image"
                  style={{ borderStyle: 'solid', borderColor: '#dee2e6', borderWidth: 'thin', borderRadius: 10, padding: 10, width: 150, maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                />
              </div>
            </Col>
          ) : null}
        </React.Fragment>
      );
    }
    if (isValueTier(row)) {
      return (
        <React.Fragment>
          {viewField('Cashback type', row.cashback_type && cap(row.cashback_type))}
          {row.cashback_type === 'flat'
            ? viewField('Cashback amount (₹)', row.cashback_value)
            : (
              <React.Fragment>
                {viewField('Cashback (%)', row.cashback_percent, row.cashback_type === 'minimum' ? 4 : 6)}
                {row.cashback_type === 'minimum' ? viewField('Guaranteed minimum (₹)', row.cashback_floor, 4) : null}
              </React.Fragment>
            )}
        </React.Fragment>
      );
    }
    return viewField('Coupon value (₹)', row.coupon_value);
  };

  const head = (o, updateDirection) => customHead(o, updateDirection);
  const plain = { filter: false, sort: false, customHeadRender: head };
  const columns = [
    { name: "product", label: "Scope", options: plain },
    { name: "seller", label: "Seller", options: plain },
    { name: "type", label: "Type", options: plain },
    { name: "value", label: "Reward", options: plain },
    { name: "budget", label: "Budget (spent/total)", options: plain },
    { name: "orders", label: "Orders (used/limit)", options: plain },
    { name: "validity", label: "Validity", options: plain },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        filterType: 'custom',
        sort: false,
        customHeadRender: head,
        customFilterListRender: () => false,
        filterOptions: {
          names: [],
          logic() { },
          display: () => (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 240, paddingTop: 8 }}>
              <TextField
                select label="Status" value={draft.status}
                onChange={(e) => setDraftField('status', e.target.value)}
                fullWidth variant="standard"
              >
                {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{cap(s)}</MenuItem>)}
              </TextField>
              <TextField
                select label="Type" value={draft.type}
                onChange={(e) => setDraftField('type', e.target.value)}
                fullWidth variant="standard"
              >
                {TYPE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{cap(t)}</MenuItem>)}
              </TextField>
              <TextField
                select label="Level" value={draft.tier}
                onChange={(e) => setDraftField('tier', e.target.value)}
                fullWidth variant="standard"
              >
                {TIER_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>{TIER_LABEL[t]}</MenuItem>
                ))}
              </TextField>
              <TextField
                type="date" label="From" value={draft.from}
                onChange={(e) => setDraftField('from', e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth variant="standard"
              />
              <TextField
                type="date" label="To" value={draft.to}
                onChange={(e) => setDraftField('to', e.target.value)}
                InputLabelProps={{ shrink: true }} fullWidth variant="standard"
              />
            </Box>
          )
        }
      }
    },
    { name: "action", label: "Action", options: plain }
  ];

  const options = {
    searchPlaceholder: 'Press Enter to Search',
    filterType: 'dropdown',
    responsive: 'scrollMaxHeight',
    print: false,
    download: false,
    selectableRows: false,
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    search: true,
    searchText: searchedText,
    viewColumns: false,
    rowHover: false,
    filter: true,
    sort: false,
    rowsPerPage: PER_PAGE,
    rowsPerPageOptions: [],
    count: total,
    serverSide: true,
    page: page,
    fixedHeader: false,
    textLabels: { filter: { all: "", title: "FILTERS", reset: "" } },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      return (
        <div className="p-2">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button fullWidth type="button" variant="contained" color="primary" onClick={() => { applyDraft(); applyNewFilters(); }}>APPLY</Button>
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth type="button" variant="outlined" onClick={() => { resetDraft(); applyNewFilters(); }}>RESET</Button>
            </Grid>
          </Grid>
        </div>
      );
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          changePage(tableState.page);
          break;
        case 'search':
          onkeypress = (e) => {
            if (e.key === 'Enter') {
              handleSearch(tableState.searchText);
            }
          };
          break;
        case 'onSearchClose':
          handleSearch('');
          break;
        default:
          break;
      }
    },
    customFooter: (count, pg, rowsPerPage, changeRowsPerPage, changePg) => {
      return <div className="p-4 d-flex justify-content-end">
        <PaginationCount page={pg} rowsPerPage={rowsPerPage} count={count} />
        <Pagination count={Math.ceil(count / rowsPerPage)} page={pg + 1} onChange={(e, p) => { changePg(p - 1) }} color="primary" />
      </div>;
    }
  };

  let data = [];
  rows.forEach((row) => {
    data.push([
      scopeCell(row),
      // a platform campaign belongs to no seller - it is Medimny's own
      row.tier === 'platform'
        ? <em className="text-muted">Medimny</em>
        : (row.seller_id && row.seller_id.company_name ? row.seller_id.company_name : '-'),
      <span style={{ textTransform: 'capitalize' }}>{row.type}</span>,
      valueCell(row),
      `${money(row.budget_spent)} / ${row.budget_total > 0 ? money(row.budget_total) : '∞'}`,
      `${row.order_count_used || 0} / ${row.order_count_limit > 0 ? row.order_count_limit : '∞'}`,
      validity(row),
      <div className={`badge text-uppercase ${statusStyle(row.status)}`}>{row.status}</div>,
      row.status === 'pending'
        ? <ButtonGroup color="primary" aria-label="outlined primary button group">
            <Button className={'text-success'} onClick={() => askApprove(row)}>Approve</Button>
            <Button className={'text-danger'} onClick={() => { setRejectFor(row); setRejectReason(''); }}>Reject</Button>
          </ButtonGroup>
        // A platform draft is not live yet - the admin may still Edit its terms, then Launches it
        // (goes active) or Aborts it. Editing is a draft-only, platform-only privilege: once launched
        // the terms are locked, and seller-authored campaigns are edited by the seller, not here.
        : row.status === 'draft'
          ? <ButtonGroup color="primary" aria-label="edit, launch or abort campaign">
              {row.tier === 'platform' ? <Button className={'text-primary'} onClick={() => setEditRow(row)}>Edit</Button> : null}
              <Button className={'text-success'} onClick={() => askLaunch(row)}>Launch</Button>
              <Button className={'text-danger'} onClick={() => askAbort(row)}>Abort</Button>
            </ButtonGroup>
        : row.status === 'active'
          ? <ButtonGroup color="primary" aria-label="pause or close campaign">
              <Button className={'text-warning'} onClick={() => askPause(row)}>Pause</Button>
              <Button className={'text-danger'} onClick={() => askClose(row)}>Close</Button>
            </ButtonGroup>
        // A paused campaign is still live, just halted - the admin can resume it or close it outright.
        : row.status === 'paused'
          ? <ButtonGroup color="primary" aria-label="resume or close campaign">
              <Button className={'text-success'} onClick={() => askResume(row)}>Resume</Button>
              <Button className={'text-danger'} onClick={() => askClose(row)}>Close</Button>
            </ButtonGroup>
          : <span>-</span>
    ]);
  });

  // Why the confirm button is disabled, or null when it is not. A confirmation with no input is
  // never blocked - only the ones that collect a value can be.
  const confirmInputError = confirm && confirm.input ? confirm.input.validate(confirmInput) : null;
  // Derived once so the button's disabled state and its styling cannot disagree - the whole failure
  // this pair guards against is a button that is disabled but does not look it.
  const confirmDisabled = !!confirmInputError;

  return (
    <React.Fragment>
      {loader ? <div className="loader-view" style={{ height: 'calc(100vh - 259px)' }}>
        <CircularProgress />
      </div> : null}

      {loader ? null :
        <MUIDataTable
          // Top-left of the table, styled like "Add Product+" in the Product Bank.
          title={
            <Button className={'text-primary'} onClick={() => setCreateOpen(true)}>
              Create platform campaign+
            </Button>
          }
          data={data}
          columns={columns}
          options={options}
        />
      }

      {/* The only campaign the ADMIN authors. Product and seller campaigns are created by sellers
          and merely approved here; this one Medimny funds itself. It is created as a draft and then
          Launched (or Aborted) from the list - it does NOT go live on create. The SAME dialog edits a
          draft before launch (opened from the row's Edit action): only the envelope and the locked
          reward type differ. */}
      <Dialog open={createOpen || !!editRow} onClose={() => { setCreateOpen(false); setEditRow(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{editRow ? 'Edit platform campaign' : 'Create platform campaign'}</DialogTitle>
        <Form
          onSubmit={editRow ? submitEdit : submitPlatform}
          initialValues={editRow ? platformInitialValues(editRow) : EMPTY_PLATFORM}
          render={({ handleSubmit, values, invalid, submitting }) => {
            const isEditing = !!editRow;
            const isGift = values.type === 'gift';
            const isFlat = values.cashback_type === 'flat';
            const isMinimum = values.cashback_type === 'minimum';
            // Both percentage cashback types (proportionate and percentage-with-floor) carry a max
            // cashback ceiling, which bounds what ONE order can cost. isProportionate is the pure
            // percentage (no floor); isPercentType is either.
            const isProportionate = !isGift && !isFlat && !isMinimum;
            const isPercentType = isProportionate || isMinimum;
            // A platform CASHBACK carries all three caps and closes on whichever it reaches first.
            // The order count is the admin's to set, bounded by what the budget could pay out on -
            // it was briefly a disabled, derived display figure that closed nothing.
            const isCashback = !isGift;
            const budgetIsACap = Math.round(Number(values.budget_total)) > 0;
            // The most orders the budget could pay this cashback on - the ceiling on the count
            // below, and what the field is filled in with while it is empty.
            const cashbackFundsOrders = isCashback ? derivedCashbackOrderLimit(values) : '';
            // At least one cap has to be set. It is a rule about the three fields TOGETHER, not
            // about any one of them, so it cannot live on a field: it gates the button and is
            // spelled out in red beneath the three, rather than reddening a field the admin has
            // done nothing wrong in.
            // A gift has no budget field at all, so a stale budget left over from a cashback the
            // admin switched away from must not count as its limit.
            // The budget counts as a limit only once it ROUNDS to something: the API stores it
            // rounded and then tests the stored figure, so a budget under ₹0.50 is no cap at all
            // there. Testing the raw entry here would pass the form and fail the save.
            // Any one of the three is enough - each of them closes the campaign on its own.
            const capsValid = !!(
              (isGift ? false : budgetIsACap) || values.order_count_limit || values.end_date
            );
            return (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <DialogContent>
                  <Row>
                    <Col xs={12} sm={6}>
                      {/* The reward type is fixed once created - the API edit path keeps the stored
                          type - so it is locked while editing. */}
                      <Field
                        name="type" id="platformReward" label="Reward"
                        component={ReactStrapSelectField}
                        disabled={isEditing}
                      >
                        <option value="cashback">Cashback</option>
                        <option value="gift">Gift</option>
                      </Field>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Field
                        type="number" name="min_order_value" id="platformMov"
                        label="Minimum order value (₹)"
                        component={ReactstrapTextField}
                        parse={twoDecimals}
                        validate={composeValidators(required, number0)}
                      />
                    </Col>

                    {isGift ?
                      <React.Fragment>
                        <Col xs={12}>
                          <Field
                            type="text" name="gift_description" id="platformGiftDescription"
                            label="Gift description"
                            component={ReactstrapTextField}
                            validate={required}
                          />
                        </Col>
                        <Col xs={6}>
                          <Field
                            type="number" name="gift_price" id="platformGiftPrice"
                            label="Gift price (₹)"
                            component={ReactstrapTextField}
                            parse={twoDecimals}
                            validate={composeValidators(required, number0)}
                          />
                        </Col>
                        <Col xs={6}>
                          <Field
                            type="text" name="gift_brand" id="platformGiftBrand"
                            label="Gift brand"
                            component={ReactstrapTextField}
                            validate={required}
                          />
                        </Col>
                        {/* Mandatory picture of the gift, uploaded exactly as on the seller form:
                            tap the camera, pick a file, it is posted as base64 and stored on S3 by
                            the API. Same component, same layout, same wording. */}
                        <Col xs={12} className="mt-2">
                          <Field
                            name="gift_image" id="platformGiftImage"
                            component={FieldFileInput}
                            label="Gift image" accept=".jpg, .png, .jpeg" preview
                            validate={requiredGiftImage}
                          />
                        </Col>
                      </React.Fragment>
                      :
                      <React.Fragment>
                        <Col xs={12} sm={isFlat ? 6 : 4}>
                          <Field
                            name="cashback_type" id="platformCashBackType" label="Cashback type"
                            component={ReactStrapSelectField}
                          >
                            <option value="flat">Flat (₹)</option>
                            <option value="proportionate">Percentage (%)</option>
                            <option value="minimum">Percentage with floor</option>
                          </Field>
                        </Col>
                        {isFlat ?
                          <Col xs={12} sm={6}>
                            <Field
                              type="number" name="cashback_value" id="platformDiscountAmount"
                              label="Cashback amount (₹)"
                              component={ReactstrapTextField}
                              parse={twoDecimals}
                              validate={composeValidators(required, number0, notAboveMovRule)}
                            />
                          </Col>
                          :
                          <React.Fragment>
                            <Col xs={12} sm={4}>
                              <Field
                                type="number" name="cashback_percent" id="platformDiscountPercent"
                                label="Cashback (%)"
                                component={ReactstrapTextField}
                                parse={twoDecimals}
                                validate={composeValidators(required, number0, percentRule)}
                              />
                            </Col>
                            {isMinimum ?
                              <Col xs={12} sm={4}>
                                <Field
                                  type="number" name="cashback_floor" id="platformFloor"
                                  label="Min cashback (₹)"
                                  component={ReactstrapTextField}
                                  parse={twoDecimals}
                                  validate={composeValidators(required, number0, minCashbackRule, notAboveMovRule)}
                                />
                              </Col> : null}
                            {/* Both percentage types: the ceiling a buyer earns on one order, however
                                large. Mandatory - it is what bounds the campaign's cost. */}
                            {isPercentType ?
                              <Col xs={12} sm={4}>
                                <Field
                                  type="number" name="cashback_max" id="platformCashbackMax"
                                  label="Max cashback (₹)"
                                  component={ReactstrapTextField}
                                  parse={twoDecimals}
                                  validate={composeValidators(required, number0, maxCashbackRule)}
                                />
                              </Col> : null}
                            {/* No helper line under the ceiling: what it means is in the field's label,
                                and what it must be at least is said by maxCashbackRule (and
                                minCashbackRule) in red, on the entry that breaks it. */}
                          </React.Fragment>
                        }
                        {/* Recomputed on every keystroke from the live form values, so the admin sees
                            the effect of a decimal as they type it rather than after saving. It is a
                            statement of what will happen, not a validation error - a decimal is a
                            perfectly legal entry, it just is not the figure that gets credited. */}
                        {cashbackRoundingNote(values) ?
                          <Col xs={12}>
                            <div style={{ fontSize: 12, marginTop: -8, marginBottom: 8, color: '#b26a00' }}>
                              {cashbackRoundingNote(values)}
                            </div>
                          </Col> : null}
                      </React.Fragment>
                    }

                    <Col xs={12} className="mt-2 mb-2">
                      {/* All three are real thresholds - budget, order count and end date - and any
                          one of them closes the campaign, so any one of them bounds Medimny's
                          exposure on its own. */}
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        Set at least one limit - this caps Medimny's exposure:
                      </span>
                    </Col>
                    {/* All three caps close the campaign, so the rule is stated once here for the
                        whole row rather than under each field. A gift has no budget. */}
                    <Col xs={12} className="mb-2">
                      <div style={{ fontSize: 12, color: '#6c757d' }}>
                        {isGift
                          ? 'This campaign closes automatically when the order count is reached or the end date passes - whichever comes first.'
                          : 'This campaign closes automatically when the budget runs out, the order count is reached, or the end date passes - whichever comes first.'}
                      </div>
                    </Col>
                    {/* A gift spends no rupees, so a budget means nothing on one: the order count
                        and the end date are the only thresholds it can be held to. */}
                    {!isGift ?
                      <Col xs={12} sm={4}>
                        <Field
                          type="number" name="budget_total" id="platformBudget"
                          label="Total budget (₹)"
                          component={ReactstrapTextField}
                          parse={twoDecimals}
                          validate={composeValidators(number, budgetAboveMaxRule)}
                        />
                        {/* No line under the budget saying what it funds. The order count beside it is
                            filled in with that same figure, so the budget's reach is already on the
                            form as a number that can be acted on rather than as prose. */}
                      </Col> : null}
                    <Col xs={12} sm={isGift ? 6 : 4}>
                      <Field
                        type="number" name="order_count_limit" id="platformOrderCount"
                        label="Order count limit"
                        component={OrderCountField}
                        parse={twoDecimals}
                        validate={composeValidators(number, wholeOrdersRule, orderLimitWithinBudgetRule)}
                      />
                    </Col>
                    {/* Refilled whenever the budget or a cashback figure changes: a flat cashback
                        divides by its amount, a percentage one by what an order at the minimum order
                        value earns. So the field always shows the maximum the campaign may carry, and a
                        lower count typed under it stands until one of those figures moves. */}
                    {isCashback ? <PrefillOrderLimit value={cashbackFundsOrders} /> : null}
                    <Col xs={12} sm={isGift ? 6 : 4}>
                      <Field
                        type="date" name="end_date" id="platformEndDate"
                        label="End date"
                        component={ReactstrapTextField}
                      />
                    </Col>
                    {/* Sits under the whole caps row rather than beside the budget box, so it reads
                        the same whether the row is three fields wide or two. Never shown on a gift,
                        which has no budget field to have typed a decimal into. */}
                    {!isGift && budgetRoundingNote(values) ?
                      <Col xs={12}>
                        <div style={{ fontSize: 12, marginTop: -8, marginBottom: 8, color: '#b26a00' }}>
                          {budgetRoundingNote(values)}
                        </div>
                      </Col> : null}
                    {!capsValid ?
                      <Col xs={12}>
                        <Label className="text-danger">
                          {isGift
                            ? 'Set at least one limit (order count or end date). This is Medimny\'s money.'
                            : 'Set at least one limit (budget, order count or end date). This is Medimny\'s money.'}
                        </Label>
                      </Col> : null}

                    {/* What each cashback type means, so the admin picks the right one. Only relevant when the
                        reward IS a cashback - a gift has no type. Styled as the app's info callout: a tinted
                        box with muted helper text. */}
                    {!isGift &&
                      <Col xs={12} className="mt-2">
                        <Box className="p-2" style={{ background: '#eef3fb', borderRadius: 4, fontSize: 12.5 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Cashback types</div>
                          <div className="text-muted" style={{ lineHeight: 1.7 }}>
                            <div><strong>Flat (₹)</strong> - a fixed rupee amount off every qualifying order (e.g. ₹200 off).</div>
                            <div><strong>Percentage (%)</strong> - a percentage of the order value (e.g. 5% off a ₹4,000 order = ₹200).</div>
                            <div><strong>Percentage with floor</strong> - a percentage, but never less than a guaranteed minimum (e.g. 5%, but at least ₹150).</div>
                          </div>
                        </Box>
                      </Col>
                    }
                  </Row>
                </DialogContent>
                <DialogActions className="pr-4">
                  <Button color="secondary" onClick={() => { setCreateOpen(false); setEditRow(null); }}>Cancel</Button>
                  <Button type="submit" color="primary" disabled={invalid || !capsValid || submitting}>
                    {submitting ? 'Saving…' : (isEditing ? 'Save changes' : 'Create Campaign')}
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        />
      </Dialog>

      {/* Read-only detail of any campaign, opened by clicking its scope. Laid out like the
          create-campaign popup (boxed reactstrap fields), and covers every tier/type - product,
          seller and platform - which the create form itself does not, being platform-only. */}
      <Dialog open={!!viewRow} onClose={() => setViewRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Campaign details</DialogTitle>
        <DialogContent>
          {viewRow ?
            <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}>
              <Row>
                {viewField('Level', TIER_LABEL[viewRow.tier] || cap(viewRow.tier))}
                {viewField('Reward', cap(viewRow.type))}
                {viewRow.tier !== 'platform'
                  ? viewField('Seller', viewRow.seller_id && viewRow.seller_id.company_name)
                  : viewField('Seller', 'Medimny')}
                {viewRow.tier === 'product' ? viewField('Listing', viewRow.product_id && viewRow.product_id.name, 12) : null}
                {/* Lot size drives the per-lot coupon maths, so surface it for product campaigns. */}
                {viewRow.tier === 'product' ? viewField('Lot size (min order quantity)', viewRow.inventory_id && viewRow.inventory_id.min_order_quantity) : null}
                {isValueTier(viewRow) ? viewField('Minimum order value (₹)', viewRow.min_order_value) : null}
                {rewardFields(viewRow)}
                {/* A gift campaign has no monetary budget - the reward is the item itself - so the
                    budget fields are meaningless there and are dropped, leaving order-limit/end-date
                    to fill the row. */}
                {viewRow.type !== 'gift' ? viewField('Total budget (₹)', viewRow.budget_total > 0 ? viewRow.budget_total : '∞', 4) : null}
                {viewField('Order count limit', viewRow.order_count_limit > 0 ? viewRow.order_count_limit : '∞', viewRow.type === 'gift' ? 6 : 4)}
                {viewField('End date', viewRow.end_date ? moment(viewRow.end_date).format('YYYY-MM-DD') : 'No end', viewRow.type === 'gift' ? 6 : 4)}
                {viewRow.type !== 'gift' ? viewField('Budget (spent/total)', `${money(viewRow.budget_spent)} / ${viewRow.budget_total > 0 ? money(viewRow.budget_total) : '∞'}`) : null}
                {viewField('Orders (used/limit)', `${viewRow.order_count_used || 0} / ${viewRow.order_count_limit > 0 ? viewRow.order_count_limit : '∞'}`)}
                {viewField('Status', cap(viewRow.status))}
                {viewField('Validity', validity(viewRow))}
                {viewRow.status === 'rejected' && viewRow.rejection_reason ? viewField('Rejection reason', viewRow.rejection_reason, 12) : null}
                {/* Why it ended, for a campaign that has. Free text when a seller or admin stopped it
                    early, one of the fixed system strings when a threshold closed it - so the label
                    names who ended it and the value says why. Absent on rows closed before the field
                    existed, which fall through to nothing rather than to a blank row. */}
                {['closed', 'expired'].includes(viewRow.status) && viewRow.close_reason
                  ? viewField(
                      `Closed by ${viewRow.closed_by || 'system'}`,
                      viewRow.close_reason,
                      12
                    )
                  : null}
              </Row>
            </fieldset> : null}
        </DialogContent>
        <DialogActions className="pr-4">
          <Button color="primary" onClick={() => setViewRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectFor} onClose={() => setRejectFor(null)} fullWidth={true}>
        <DialogTitle>Reject campaign</DialogTitle>
        <DialogContent className="mb-0">
          {/* Boxed reactstrap control, same as the create form and the detail popup's viewField - a
              floating-label MUI input here would be the only one of its kind on the screen. */}
          <FormGroup className="mt-2">
            <Label for="rejection_reason">Reason (optional)</Label>
            <Input
              type="textarea" rows={2} autoFocus
              id="rejection_reason" name="rejection_reason"
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions className="pr-4 pt-0">
          <Button color="primary" onClick={() => setRejectFor(null)}>Cancel</Button>
          <Button className={'text-danger'} onClick={submitReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Close, with its optional reason. Separate from the generic confirmation below because that
          one's input is numeric (it takes a gift delivery cost), and separate from Reject above
          because the two write to different fields and carry different warnings. */}
      <Dialog open={!!closeFor} onClose={() => setCloseFor(null)} fullWidth={true}>
        <DialogTitle>Close campaign</DialogTitle>
        <DialogContent className="mb-0">
          <DialogContentText>
            This ends the campaign immediately and cannot be undone. Buyers can no longer earn its reward.
          </DialogContentText>
          <FormGroup className="mt-3">
            <Label for="close_reason">Reason for closing (optional)</Label>
            <Input
              type="textarea" rows={2} autoFocus
              id="close_reason" name="close_reason"
              placeholder="e.g. seller requested withdrawal, terms breached"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              maxLength={500}
            />
            <FormText>{`Left blank, the campaign is recorded as "${ADMIN_DEFAULT_CLOSE_REASON}"`}</FormText>
          </FormGroup>
        </DialogContent>
        <DialogActions className="pr-4 pt-0">
          <Button color="primary" onClick={() => setCloseFor(null)}>Cancel</Button>
          <Button className="text-danger" onClick={submitClose}>
            Close campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm-before-act for every irreversible campaign action (approve, launch, close, abort).
          Running the action closes the dialog.

          A confirmation may also COLLECT a value before it will run - `confirm.input` - which is how
          approving a gift campaign takes its delivery cost. The confirm button is disabled until that
          value validates, so the dialog cannot submit something the API would refuse. */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle>{confirm ? confirm.title : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm ? confirm.message : ''}</DialogContentText>
          {confirm && confirm.input ?
            <FormGroup className="mt-3">
              <Label for="confirm_input">{confirm.input.label}</Label>
              <Input
                type="number" autoFocus
                id="confirm_input" name="confirm_input"
                value={confirmInput}
                // Capped at 2 decimals like every other numeric entry on this screen. This one is not
                // a final-form Field, so the same helper is applied by hand on the way into state - a
                // third decimal is dropped rather than stored and then rejected.
                onChange={(e) => setConfirmInput(twoDecimals(e.target.value) || '')}
                min={GIFT_DELIVERY_COST_STEP}
                step={GIFT_DELIVERY_COST_STEP}
                // An untouched field is not an error - it just leaves the button disabled. The field
                // only turns red once the admin has actually typed something unacceptable.
                invalid={confirmInput !== '' && !!confirmInputError}
              />
              {/* The message REPLACES the help text rather than stacking under it, as it did when
                  this was a single MUI helperText - the rule that was broken is more use at that
                  moment than the rules in full. */}
              {confirmInput !== '' && confirmInputError
                ? <FormFeedback>{confirmInputError}</FormFeedback>
                : <FormText>{confirm.input.help}</FormText>}
            </FormGroup> : null}
        </DialogContent>
        <DialogActions className="pr-4">
          <Button color="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
          {/* The colour class is dropped while the button is disabled, and that is load-bearing, not
              cosmetic: Bootstrap's `.text-success`/`.text-danger` set `color` with `!important`, which
              MUI's disabled styling cannot override. Keeping the class on a disabled button leaves it
              looking solid green and perfectly clickable while it silently ignores every click - so
              the admin cannot tell the delivery cost is what is holding them up. Without the class,
              MUI greys it out and the state is visible. */}
          <Button
            className={confirmDisabled ? '' : (confirm && confirm.danger ? 'text-danger' : 'text-success')}
            disabled={confirmDisabled}
            onClick={() => {
              // Re-checked rather than trusted to `disabled` alone. This is the click that decides
              // what a seller is billed per gift order for the life of the campaign, and the cost of
              // the extra line is nothing next to approving one at a figure that never validated.
              if (confirmDisabled) return;
              const c = confirm; const v = confirmInput;
              setConfirm(null);
              if (c) c.run(v);
            }}
          >
            {confirm ? confirm.confirmLabel : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default ProductCouponsList;
